import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { API, useAuth } from "../context/AuthContext";
import { ArrowLeft, Target, HeartHandshake } from "lucide-react";
import { toast } from "sonner";

const DOMAIN_META = {
  linguistic: { label: "Linguistic", color: "bg-[#E6F0E9] text-[#2C4C3B]", desc: "Vocabulary, syntax and grammar use." },
  social: { label: "Social", color: "bg-[#FCEAE5] text-[#9C422A]", desc: "Initiating, turn-taking, social pragmatics." },
  strategic: { label: "Strategic", color: "bg-[#F4F5F0] text-[#52606D]", desc: "Repair, request help, navigate device." },
  operational: { label: "Operational", color: "bg-[#E2E8F0] text-[#1A202C]", desc: "Device handling, charging, volume." },
};

export default function GoalResults() {
  const { id } = useParams();
  const { authHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/goal-history/${id}`, { headers: authHeaders() });
        setData(res.data);
      } catch {
        toast.error("Could not load goal bank");
      }
    })();
    // eslint-disable-next-line
  }, [id]);

  const groups = useMemo(() => {
    if (!data) return null;
    const byDomain = { linguistic: [], social: [], strategic: [], operational: [] };
    data.goals.forEach((g) => {
      if (byDomain[g.domain]) byDomain[g.domain].push(g);
    });
    return byDomain;
  }, [data]);

  if (!data) {
    return <div className="max-w-4xl mx-auto px-6 py-20 text-[#52606D]" data-testid="goal-results-loading">Loading…</div>;
  }

  const visibleDomains = filter === "all" ? Object.keys(groups) : [filter];

  return (
    <div className="max-w-6xl mx-auto px-6 py-14" data-testid="goal-results-page">
      <Link to="/goals" className="inline-flex items-center gap-2 text-sm text-[#2C4C3B] hover:underline mb-6" data-testid="goal-results-back">
        <ArrowLeft className="w-4 h-4" /> New goal bank
      </Link>

      <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]" data-testid="goal-results-context">
        {data.disorder_name} · {data.communicator_stage_title} · {data.severity_label}
      </span>
      <h1 className="font-display text-4xl font-semibold text-[#2C4C3B] mt-2" data-testid="goal-results-heading">
        {data.activity_name}
      </h1>
      <p className="text-[#52606D] mt-1 text-sm">
        Device: <span className="font-medium text-[#2C4C3B]">{data.device === "avaz" ? "Avaz AAC" : "Generic AAC"}</span> · {data.goals.length} goals
      </p>

      {data.is_trial && (
        <div className="mt-6 p-5 rounded-2xl bg-[#2C4C3B] text-white flex items-start gap-3 flex-wrap" data-testid="goal-trial-banner">
          <div className="flex-1 min-w-[260px]">
            <div className="text-xs tracking-[0.2em] uppercase text-[#A8C9AC]">Free trial preview</div>
            <p className="text-sm mt-1">You're viewing {data.goals.length} sample goals. Subscribe to unlock all <strong>12 SMART goals</strong> across linguistic, social, strategic, and operational domains.</p>
          </div>
          <Link to="/plans" className="px-5 py-2.5 rounded-full bg-[#C66A51] hover:bg-[#A85639] text-white text-sm font-semibold" data-testid="goal-trial-subscribe">
            Subscribe to unlock →
          </Link>
        </div>
      )}

      {/* Disclaimer */}
      <div className="mt-8 p-5 rounded-2xl bg-[#E6F0E9] border border-[#2C4C3B]/15 flex items-start gap-3" data-testid="goal-results-disclaimer">
        <HeartHandshake className="w-5 h-5 text-[#2C4C3B] mt-0.5 flex-shrink-0" />
        <p className="text-sm text-[#2C4C3B] leading-relaxed">
          <span className="font-semibold">Goals below are reference templates.</span> Co-author each goal with the individual and their family — presume competence,
          honour interests and sensory needs, and follow neurodiversity-affirming practice. Avoid prompts like "Say hi" or "Tell me what this is"; adapt to the person's age, stage, and support needs.
          <span className="block mt-1 font-medium">Connection first. Communication always.</span>
        </p>
      </div>

      {/* Filter chips */}
      <div className="mt-8 flex items-center gap-2 flex-wrap">
        {["all", "linguistic", "social", "strategic", "operational"].map((f) => (
          <button key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-sm capitalize transition-colors
              ${filter === f ? "bg-[#2C4C3B] text-white" : "bg-white text-[#2C4C3B] border border-[#E2E8F0] hover:bg-[#E6F0E9]"}`}
            data-testid={`goal-filter-${f}`}>
            {f}{f !== "all" && groups[f] ? ` (${groups[f].length})` : ""}
          </button>
        ))}
      </div>

      {/* Domain sections */}
      {visibleDomains.map((domain) => {
        const meta = DOMAIN_META[domain];
        const items = groups[domain] || [];
        if (items.length === 0) return null;
        return (
          <section key={domain} className="mt-12" data-testid={`goal-domain-${domain}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-xl ${meta.color} flex items-center justify-center`}>
                <Target className="w-5 h-5" />
              </div>
              <div>
                <h2 className="font-display text-2xl text-[#2C4C3B]">{meta.label} Competency</h2>
                <p className="text-xs text-[#52606D]">{meta.desc}</p>
              </div>
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              {items.map((g, i) => (
                <article key={i} className="p-6 rounded-2xl bg-white border border-[#E2E8F0] card-lift fade-up"
                  style={{ animationDelay: `${i * 40}ms` }}
                  data-testid={`goal-${domain}-${i}`}>
                  <div className="flex flex-wrap items-center gap-2 text-xs tracking-[0.15em] uppercase">
                    <span className={`px-2.5 py-1 rounded-full ${meta.color}`}>{meta.label}</span>
                    <span className="text-[#52606D]">{g.communicative_function}</span>
                    <span className="text-[#52606D]">·</span>
                    <span className="text-[#52606D]">{g.prompt_level}</span>
                  </div>
                  <p className="mt-3 text-[#1A202C] leading-relaxed">{g.target}</p>
                  <div className="mt-4 flex flex-wrap gap-3 text-xs text-[#52606D]">
                    <span><span className="font-medium text-[#2C4C3B]">Frequency:</span> {g.frequency}</span>
                    <span>·</span>
                    <span><span className="font-medium text-[#2C4C3B]">Timeframe:</span> {g.timeframe}</span>
                  </div>
                </article>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
